import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Layout from 'components/layout';
import Head from 'components/head';
import { Box, Heading, Text, Flex, AspectRatio, List, ListItem, ListIcon, Button } from '@chakra-ui/react';
import Proyectos from '../../components/proyectos';
import { VideoSchema } from '../../helpers/schemaGenerator';
import ContactBanner from '../../components/contactBanner';
import { ChevronRightIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { StaticImage } from 'gatsby-plugin-image';
import ogImg from '../../../content/images/servicios/Recorrido-virtual-arquitectonico3.jpg';

const Arquitectura = ({ lang='es' }) => {
  const l = (...texts) => lang === 'en' ? texts[1] : texts[0]

  const pageTitle = l('🏗️ Recorrido Virtual Arquitectónico interactivo', '🏗️ Interactive Virtual Architectonic tours')
  const pageImage = 'https://visualma.com' + ogImg
  const pageDescription =
    l('Soluciones de visualización arquitectónica y recorridos virtuales para mejorar ventas y ahorrar costos. Recorridos virtuales en VR, 360º, móvil y web en México',
    'Visualization solutions for architecture and virtual tours that boost sales and cut costs. Virtual tours in VR, 360º, mobile devices and Web.')
  const pageUrl = l(
    'https://visualma.com/arquitectura-recorrido-virtual',
    'https://visualma.com/en/architecture-virtual-tours')

  const MedImgProps = {
    layout: 'constrained',
    width: 800,
    height: 450,
    formats: ['auto', 'webp', 'avif'],
  }

  return (
    <Layout lang={lang}>
      <Head lang={lang}
        pageTitle={pageTitle}
        imageUrl={pageImage}
        pageDescription={pageDescription}
      />
      <VideoSchema
        videoUrl="https://player.vimeo.com/video/472419296"
        contentUrl={pageUrl}
        imageUrl={pageImage}
        title={pageTitle}
        description={
          l('Ejemplo de recorridos virtual arquitectónico interactivo en México',
          'Example of virtual architectonic interactive tours')
        }
        date="2019-05-27T17:56:39Z"
        duration="PT5M21S"
      />

      <div>
        {/* ----- HeroImage ----- */}
        <Flex justifyContent="flex-end">
          <h2 className="pageHeader">
            {l('Recorrido Virtual Arquitectónico',
              'Virtual Architectonic Tours')}
          </h2>
        </Flex>

        <StaticImage
          className="headerImg"
          layout="fullWidth"
          formats={['auto', 'webp', 'avif']}
          loading="eager"
          src="../../../content/images/servicios/Recorrido-virtual-arquitectonico3.jpg"
          alt={l('Recorridos arquitecnoico virtual en México', 'Virtual Reality Architectonic tour')}
        />

        <Flex className="headerTitleWrapper">
          <div>
            <h1 className="parrafo parrafoGrande headerTitle">
              {l(<><b>Recorridos Virtuales Arquitectónicos</b> interactivos <Box display={['none', 'inline-block']}>{' '}y fotorealistas</Box></>,
                <><Box display={['none', 'inline-block']}>Photorealistic</Box><b>{' '}Virtual Architectonic tours</b></>)}
            </h1>
          </div>
        </Flex>
        {/* ----------------------- */}

        <Flex
          bg="transparent"
          justify="flex-start"
          direction="column"
          m="2em auto 5em"
          maxWidth="50em"
        >
          <Heading as="h2" className="subHeader" mb="0.5em">
            {l('¡Aumenta tus Ventas y Reduce tus costos!', 'Boost your sales and reduce your costs!')}{' '}
            <span role="img" aria-label="Grua" style={{marginLeft: '24px'}}>
              🏗️
            </span>
          </Heading>
        </Flex>

        <AspectRatio
          maxWidth="100%"
          style={{ margin: '0em auto 2em' }}
          ratio={16 / 9}
        >
          <Box
            as="iframe"
            loading="lazy"
            title={l('Recorridos virtuales arquitectónicos interactivos', 'Interactive architectonic virtual tour demo')}
            src="https://player.vimeo.com/video/472419296?autoplay=true&loop=true&muted=true"
            allowFullScreen
            allow="accelerometer; autoplay;"
            borderRadius={[0, 0, 0, '8px']}
          />
        </AspectRatio>

        <hr style={{ marginTop: '2em', borderColor: 'var(--accent-transparent)' }} />

        <Flex
          bg="transparent"
          justify="flex-start"
          direction="column"
          m="3em auto 0"
          maxWidth="50em"
        >
          <Heading as="h2" className="subHeader" mb="0.5em">
            {l('Visualización Arquitectónica', 'Architectonic Visualization')}
          </Heading>
        </Flex>

        <p className="parrafo">
          {l('Muestra tus proyectos arquitectónicos de manera fácil e intuitiva con la posibilidad de interactuar con el entorno, modificar acabados y decoración.',
            'Showcase your architectonic projects in an easy and intuitive way with the posibility of interacting with the environment and modify colors, furniture, decoration and materials.')}
          <br /><br />
          {l('Impresiona a tus clientes en un showroom o remotamente desde cualquier teléfono o computadora, en realidad virtual y en realidad aumentada.',
            'Impress your clients with a showroom or remotely from their own phone, tablet or computer, use virtual reality and augmented reality to close sales.')}
          <br /><br />
        </p>

        <div className="contenidoSmall" style={{ marginBottom: '4em' }}>
          <StaticImage
            {...MedImgProps}
            width={1024}
            src="../../../content/images/servicios/arquitectura/mapa-recorrido-virtual3_c.jpg"
            alt="Aplicación de visualización arquitectónica con menús para seleccionar materiales"
          />
          <Text m="2em auto 0" fontSize="2xl" className="parrafo">
            {l('Soluciones diseñadas para facilitar los procesos de ventas, pre-ventas, desarrollo y planeación de proyectos arquitectónicos.',
              'Solutions designed to make pre-sale and sale processe\'s easier and help with the development and planning of architectural projects.')}
            <br /><br />
            {l('Podemos ayudarte en cualquier etapa del proyecto, desde conceptualización hasta obra terminada. ',
              'We can help you in every stage of the project, from the conceptualization and planning to the finished product.')}
          </Text>
        </div>

        <Flex className="flexList flexListLeft">
          <Flex className="columna"  >
            <Heading as="h3">
              {l('Facilidad de uso', 'Ease of use')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Tus clientes podrán navegar el mundo virtual de manera intuitiva y sencilla explorando sitios de interés y modificándolos a su gusto.',
                'Your clients will be able to navegate the virtual world in an easy and intuitive way, exploring sites of interest and personalizing them in real time.')}
              <br /><br />
              {l('Tenemos diferentes sistemas de navegación para cada dispositivo ya sean lentes de realidad aumentada, realidad virtual, Pc, tabletas o teléfonos.',
                'We have different navigation systems for each device, from augmented reality lenses, virtual reality HMD, PCs, tablets or phones.')}
            </Text>
          </Flex>
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/TabletMockupWebArquitectura.jpg"
              alt={l('Render 3d de showroom de bienes raíces en realidad virtual', '3D render of a real state showroom in virtual reality')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>

          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/interfaz-visualizacion-arquitectonica.jpg"
              alt={l('Aplicación de visualización arquitectónica con menús para seleccionar materiales', 'Visualization Application for architecture with menus to change materials')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna">
            <Heading as="h3">
              {l('Personalización en tiempo real', 'Real time personalization')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Nuestro desarrollo permite adaptar la decoración, inmobiliario y diseño de interiores al gusto del cliente.',
                'Our software allows the client to adapt the decoration, furniture colors and materials to their taste.')}
            </Text>
            <List spacing={3} className="listaConIconos">
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Cambio de mobiliario y ornamentación',
                  'Change of furniture and accessories')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Modificación de acabados, materiales, pintura',
                  'Customization of colors, materials and floors')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Iluminación modificable.',
                  'Lightning customization')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Modificación de condiciones atmosféricas: Día, noche, lluvia, temperatura.',
                  'Personalization of atmospheric conditions: Day, night, rain, temperature.')}
              </ListItem>
            </List>
          </Flex>


          <Flex className="columna">
            <Heading as="h3">
              {l('Todo tipo de Soluciones', 'All kind of solutions')}
            </Heading>
            <List spacing={3} className="listaConIconos">
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Showrooms corporativo con proyección inmersivas',
                    'Corporative showrooms with inmersive projections')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Realidad Virtual (VR) para Showrooms y oficinas de ventas',
                    'Virtual Reality (VR) for showrooms and sales offices')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Realidad Virtual (VR) para exposiciones ambulantes y oficinas remotas',
                    'Virtual Reality (VR) for itinerant exhibitions and remote offices')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Recorridos virtuales en Kioskos Touch y Tabletas',
                    'Virtual tours in Touch kiosks and Tablets')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Recorridos virtuales con tecnologías Web',
                    'Virtual tours with web technology')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Aplicación de escritorio para generación de renders in-app',
                    'Desctop application for in-app render generation.')}
              </ListItem>
            </List>
          </Flex>
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/revistaDiseñosArquitectonicos_render3d.png"
              alt={l('Revista con renders 3D arquitectónicos fotorealistas', 'Magazine with architectonic 3D renders')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>


          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/visualizacion-arquitectonica-milti-dispositivo.jpg"
              alt={l('Aplicación de visualización arquitectónica con menús para seleccionar materiales', 'Architectonic visualization app with menus to customize materials')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna">
            <Heading as="h3">
              {l('Visualización en dispositivos móviles y web', 'Visualization in mobile devices & web')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Amplía el alcance de visitas a tus desarrollos con nuestra implementación web, con todas las funcionalidades anteriormente mencionadas en una plataforma que puede ser visitada en computadoras personales, tabletas y  smart phones.',
                'Amplify your reach to potential clients with our web implementation that has all the functionalityes we have mentioned earlier and can be viewed in any personal computer, tablet and smart phone.')}
            </Text>
            <Button 
              as="a" 
              target="_blank" 
              href="https://archvis-demo.visualma.com/"
              size="md"
              m="2em auto 0 auto"
              maxWidth="300px"
              variant="solid"
              colorScheme="azulin"
              rightIcon={<ExternalLinkIcon />}
            >
              {l('Ver Demo de versión web', 'View Web Version Demo')}
            </Button>
          </Flex>


          <Flex className="columna">
            <Heading as="h3">
              {l('Genera tus Renders Arquitectónicos', 'Generate your architectonic renders')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Permite generar imágenes en alta resolución 4K ideales para contenidos publicitarios de redes sociales o impresos, hasta imágenes 360°. Una herramienta ideal para generar tus propios contenidos publicitarios.',
                'Allows the generation of unlimited 4K high resolution images that are ideal for all your marketing content. ')}
            </Text>
          </Flex>
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/aplicacion-genera-renders-arquitectonicos.jpg"
              alt={l('Render 3d de cuarto con una sala y una interfaz que permite generar renders arquitectónicos', '3D render of a room with a sofa and an interface that allows to generate high quality architectonic renders')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>


          
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/lentes-realidad-virtual-gear-oculus-vrSm2.jpg"
              alt={l('Render 3D de recamara con cama blanca en aplicación de visualización arquitectónica', '3D render of a room with a white bed')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna">
              <Heading as="h3">
                {l('Realidad Virtual Arquitectónica', 'Virtual Reality for Architecture')}
              </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Permite vivir la experiencia de algo que todavía no ha sido construido en el espacio real, a escala humana, como si estuvieras ahí. Permite simular múltiples condiciones atmosféricas y de iluminación, modificar acabados, muebles y decoración para adaptarse a los gustos del cliente, y concentrar información de ventas en tiempos ágiles y eficientes.',
                'It allows living the experience of something that has not been build yet, in real scale and realistic rendering. You can simulate climate conditions, lighting, materials, furniture to adapt to the taste of the client and make sales faster and more efficient')}
            </Text>
          </Flex>

          <Flex className="columna">
            <Heading as="h3">
              {l('Video Tours', 'Video Tours')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Generamos un increíble video guiado que resalta las mejores características de tu proyecto. Ideal para complementar estrategias publicitarias y como showcase de las generalidades de tu desarrollo en una manera breve, concisa e impactante.',
                'We generate an incredible video tour that higlights the best characteristics of your projects. It is ideal to complement marketing strategies and to showcase your development in a concise and impactful way.')}
            </Text>
          </Flex>
          <Flex className="columna img">
            <video
              controls={false}
              width="740px"
              loading="lazy"
              autoPlay
              loop
              playsInline
              muted
              controlsList="nodownload"
              crossOrigin="anonymous"
              alt="Video tour de bienes raíces y arquitectura"
              title="Video tour de bienes raíces y arquitectura"
              style={{ margin: '0 auto', width: '720px', maxWidth: '100%' }}
            >
              <source src="https://player.vimeo.com/external/473985446.sd.mp4?s=2314ad981fb00ca32cb8279ff8e8b06d6a293286&profile_id=165"
                type="video/mp4"
              />
              Tu navegador no soporta videos.
            </video>
          </Flex>


          
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/sala-juntas-showroom-arquitectura-3d.jpg"
              alt="Aplicación de visualización arquitectónica en un teléfono"
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna">
            <Heading as="h3">
              {l('Solución Integral de Tour virtual 3d para nuevos desarrollos', 'Integral solution for 3D Virtual tours')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l(<>
                Nuestras soluciones no solo permiten que <b>tus clientes se enamoren de tus proyectos</b> desde cualquier lugar o dispositivo.
                <br /><br />
                También podrás generar una cantidad ilimitada de <b>renders 4K para tu propio material publicitario</b> desde tu oficina sin la ayuda de ningun experto.
                <br /><br />
                <b>Todo lo necesario para</b> hacer marketing de proyectos inmobiliarios y <b>cerrar ventas</b>.
                </>,
                <>
                Our solutions not only make <b>your clients fall in love with your projects</b> remotely from any device.
                <br /><br />
                You will also be able to generate an unlimited amount of <b>4k renders for your marketing material</b> from your office without the need of any expert help. 
                <br /><br />
                Everything necessary to advertise real state projects and close sales.
                </>)}
            </Text>
          </Flex>

          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/panfletos-tripticos-inmuebles.png"
              alt={l('Barda con anuncio espectacular de casa en construcción', '')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/marketing-en-redes-sociales-arquitectura.png"
              alt={l('Kiosco con aplicación interactiva en centro comercial', 'Kiosk with touch screen application in shopping mall')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>

          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/kiosco-centro-comercial.jpg"
              alt={l('Barda con anuncio espectacular de casa en construcción', 'Fence with big sign of real state development')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/revista-billboard-kiosko-espectacular-arquitectura2.png"
              alt={l('Kiosco con aplicación interactiva en centro comercial', 'Kiosk with interactive application in shopping mall')}
              style={{ maxWidth: '100%'}}
            />
          </Flex>

          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/billboard-construccion.jpg"
              alt={l('Barda con anuncio espectacular de casa en construcción', 'Billboard with big sign of real state development')}
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/anuncio-espectacular-barda-construccionCrop_comp.jpg"
              alt={l('Barda con anuncio espectacular de casa en construcción', 'Fence with big sign of real state development')}
              style={{ maxWidth: '100%'}}
            />
          </Flex>


        </Flex>

        <br />
        <ContactBanner lang={lang}/>
        <br />

        <Flex
          bg="transparent"
          justify="flex-start"
          direction="column"
          m="3em auto"
        >
          <Heading as="h2" className="subHeader" mb="2em">
            {l('¿Necesitas un proyecto personalizado?', 'Do you need a custom development')}
          </Heading>
          <Text fontSize="xl" className="parrafoChico spacer">
            {l('Para clientes con necesidades más complejas, como proyectos industriales y de ingeniería, ofrecemos la capacidad de crear funcionalidades específicas y customizaciones a la medida de sus necesidades de visualización.',
              'For clients with more especific and complex needs, like industral and engineering projects, we can create any kind of custom functionality and develop custom modules that fill the needs of your business objectives.')}
            <br /><br />
            {l('En Visualma queremos ser tus proveedores y asesorarte para que utilices las nuevas tecnologías a tu favor.',
              'In Visualma we want to advice you so you can use the new technologies in your favor.')}
          </Text>
        </Flex>

        {/* <ContactBanner /> */}

        <Flex className="flexList flexListLeft">
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/patio-recorrido-virtual-hogar.jpg"
              alt="Render 3D de recamara con cama blanca"
              style={{ maxWidth: '100%' }}
            />
          </Flex>
          <Flex className="columna img">
            <StaticImage
              {...MedImgProps}
              src="../../../content/images/servicios/arquitectura/Render-realista-arquitectura-recamara.jpg"
              alt="Render 3D de recamara con cama negra"
              style={{ maxWidth: '100%' }}
            />
          </Flex>
        </Flex>
        <Proyectos lang={lang}
          title={l('Proyectos de Arquitectura', 'Virtual Architecture Projects')}
          category="Arquitectura"
          limit={3}
        />
      </div>
    </Layout>
  );
};
Arquitectura.propTypes = {
  lang: PropTypes.string
};

export default Arquitectura;
